import React, { useState, useEffect } from 'react'
import { LayoutPage, LayoutPageHeaderStyle, Button, useNotifications } from '@wallyhealth/web-core'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useSearchParams } from 'react-router-dom'
import config from '../../config'
import intelService from '../../services/intelService'
import styles from './index.module.css'
import { useBilling } from './hooks'

const resolveStripeFontSize = () => {
  return (window.innerWidth > 768) ? 14 : 14
}

const createStripeOptions = () => {
  const fontSize = resolveStripeFontSize()

  return {
    hideIcon: true,
    style: {
      base: {
        fontSize: `${fontSize}px`,
        fontWeight: '100',
        fontSmoothing: 'antialiased',
        color: 'black',
        '::placeholder': {
          color: '#838383'
        },
        ':-webkit-autofill': {
          color: '#fce883'
        }
      },
      invalid: {
        iconColor: '#F92E32',
        color: '#F92E32'
      }
    }
  }
}

const CardUpdatePage = () => {
  const [search] = useSearchParams()
  const notifications = useNotifications()
  const stripe = useStripe()
  const elements = useElements()
  const { isUpdating, updateCard } = useBilling()

  const [zip, setZip] = useState('')

  const [isCardComplete, setIsCardComplete] = useState(false)
  const [isGeneratingToken, setIsGeneratingToken] = useState(false)

  const encodedPatientId = search.get('epid')

  if (!encodedPatientId) {
    window.location.href = 'https://www.carebywally.com'
    return null
  }

  const canSubmit = () => {
    return isCardComplete
  }

  useEffect(() => {
    intelService.recordView({ viewName: 'CardUpdatePage' })
  }, [])

  const onCardChange = ({ complete, value }) => {
    setIsCardComplete(complete)
    setZip(value.postalCode)
  }

  const onTokenGenerated = async (token) => {
    await updateCard({
      encodedPatientId,
      token
    })
  }

  const onSubmit = () => {
    if (!isGeneratingToken) {
      generateToken()
        .then(onTokenGenerated)
        .catch(onTokenError)
    }
  }

  const promisifyResult = (result) => {
    setIsGeneratingToken(false)

    return (result.error)
      ? Promise.reject(result.error)
      : Promise.resolve(result.token.id)
  }

  const generateToken = () => {
    setIsGeneratingToken(true)

    return stripe.createToken(
      elements.getElement(CardElement), {
        address_zip: zip,
        address_country: 'US',
        currency: 'usd'
      }
    ).then(promisifyResult)
  }

  const onTokenError = (error) => {
    notifications.error(error.message)
  }

  return (
    <LayoutPage
      headerStyle={LayoutPageHeaderStyle.FULL}
      title="Update your card."
      description="You can use any major credit card. Your FSA/HSA card works too.">

      <div className={styles.form}>

        <div className={styles.card}>
          <CardElement
            onChange={onCardChange}
            options={createStripeOptions()}
          />
        </div>

        <Button
          className={styles.button}
          label="Submit"
          disabled={!canSubmit()}
          onClick={onSubmit}
          isLoading={isGeneratingToken || isUpdating}
        />

      </div>
    </LayoutPage>
  )
}

const StripeEnabledCardUpdatePage = (props) => (
  <Elements
    stripe={loadStripe(config.STRIPE_PUBLISHABLE_KEY)}>
    <CardUpdatePage {...props} />
  </Elements>
)

export default StripeEnabledCardUpdatePage
